import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { CreditCard, Trash2, RefreshCw, LayoutList, BarChart3, Plus } from "lucide-react";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { PRESETS } from "@/data/presets";

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return `${date} ${time}`;
}

// Extract the base card type from a full card name for grouping in overview mode.
// "Red Supervisor 3-4" → "Red Supervisor"
// "Blue Security Card 2" → "Blue Security Card"
// "Board 3" → "Board 3"
// "Crypt" → "Crypt"
function getCardBaseName(cardName) {
  if (!cardName) return "Unknown";
  if (cardName.startsWith("Red Supervisor")) return "Red Supervisor";
  if (cardName.startsWith("Blue Security Card")) return "Blue Security Card";
  return cardName;
}

// Sort order for overview mode card types
const OVERVIEW_SORT_ORDER = [
  "Red Supervisor",
  "Blue Security Card",
  "Crypt",
  "Wasteland",
  "Last Resort",
  "Board 1",
  "Board 2",
  "Board 3",
  "Board 4",
  "Board 5",
  "Board 6",
  "Board 7",
];

function getOverviewSortIndex(baseName) {
  const idx = OVERVIEW_SORT_ORDER.indexOf(baseName);
  return idx === -1 ? OVERVIEW_SORT_ORDER.length : idx;
}

function getNicknameFromEmail(username, whitelistEntries) {
  if (!username || !username.includes("@")) return username;
  const entry = whitelistEntries.find((e) => e.email === username);
  if (entry?.nickname && entry.nickname !== "") return entry.nickname;
  return username;
}

export default function CardHoldersTab({ isAdmin, mode, currentUser, whitelistEntries = [] }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("overview"); // "overview" | "detailed"

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Delete All confirm state
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllConfirming, setDeleteAllConfirming] = useState(false);
  const [deleteAllError, setDeleteAllError] = useState(null);

  // Add Card modal state
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addFields, setAddFields] = useState({ username: "", cardName: "", location: "", proxy: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/card-holders");
      const filtered = mode ? data.filter((r) => r.mode === mode) : data;
      filtered.sort((a, b) => new Date(b.claimedAt) - new Date(a.claimedAt));
      setRecords(filtered);
    } catch (e) {
      setError("Failed to load card holder records.");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // ── Overview data ──────────────────────────────────────────────────────────
  // Group records by base card name AND user, so each (card type, user) pair
  // gets its own row with a count — prevents overcrowding when many users hold
  // the same card type.
  const overviewRows = useMemo(() => {
    const groups = {};
    for (const r of records) {
      const baseName = getCardBaseName(r.cardName);
      const username = r.username || "Unknown";
      const key = `${baseName}|${username}`;
      if (!groups[key]) {
        groups[key] = { baseName, username, count: 0 };
      }
      groups[key].count += 1;
    }
    return Object.values(groups).sort((a, b) => {
      const cardOrder =
        getOverviewSortIndex(a.baseName) - getOverviewSortIndex(b.baseName);
      if (cardOrder !== 0) return cardOrder;
      return a.username.toLowerCase() < b.username.toLowerCase() ? -1 : 1;
    });
  }, [records]);

  // ── Detailed data ──────────────────────────────────────────────────────────
  const detailedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const na = (a.username ?? "").toLowerCase();
      const nb = (b.username ?? "").toLowerCase();
      return na < nb ? -1 : na > nb ? 1 : 0;
    });
  }, [records]);

  // ── Permission helpers ─────────────────────────────────────────────────────
  const canDelete = useCallback(
    (record) => {
      if (isAdmin) return true;
      if (!currentUser) return false;
      // Match by nickname (username field on the record)
      const nick = currentUser.nickname ?? currentUser.email ?? null;
      return nick && record.username === nick;
    },
    [isAdmin, currentUser]
  );

  // ── Delete handlers ────────────────────────────────────────────────────────
  const openDelete = (record) => {
    setDeleteTarget(record);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirming(false);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteConfirming(true);
    setDeleteError(null);
    try {
      await api.delete(`/card-holders/${deleteTarget.id}`);
      setRecords((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      closeDelete();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setDeleteError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to delete record. Please try again."
      );
    } finally {
      setDeleteConfirming(false);
    }
  };

  // ── Delete All handlers ────────────────────────────────────────────────────
  const openDeleteAll = () => {
    setDeleteAllOpen(true);
    setDeleteAllError(null);
  };

  const closeDeleteAll = () => {
    setDeleteAllOpen(false);
    setDeleteAllConfirming(false);
    setDeleteAllError(null);
  };

  const handleDeleteAll = async () => {
    setDeleteAllConfirming(true);
    setDeleteAllError(null);
    try {
      await api.delete("/card-holders/all");
      setRecords([]);
      closeDeleteAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setDeleteAllError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to delete all records. Please try again."
      );
    } finally {
      setDeleteAllConfirming(false);
    }
  };

  // ── Add Card handlers ──────────────────────────────────────────────────────
  const openAddCard = () => {
    const nick = currentUser?.nickname ?? currentUser?.email ?? "";
    const firstPreset = PRESETS[0];
    setAddFields({
      username: nick,
      cardName: firstPreset.cardOptions[0],
      location: firstPreset.source,
      proxy: "",
    });
    setAddError(null);
    setAddCardOpen(true);
  };

  const closeAddCard = () => {
    setAddCardOpen(false);
    setAddFields({ username: "", cardName: "", location: "", proxy: "" });
    setAddError(null);
  };

  const handleAddCard = async () => {
    if (!addFields.cardName || !addFields.location) {
      setAddError("Card name and location are required.");
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const payload = {
        claimedBy: addFields.username || undefined,
        cardName: addFields.cardName,
        location: addFields.location,
        proxyClaimedBy: addFields.proxy || null,
        ...(mode ? { mode } : {}),
      };
      await api.post("/card-holders", payload);
      await loadRecords();
      closeAddCard();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAddError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to create record."
      );
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <div data-testid="card-holders-tab">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500">
            Card Holders
          </span>
          {mode && (
            <span
              className={
                mode === "private"
                  ? "text-[9px] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
                  : "text-[9px] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-orange-500/40 text-orange-400 bg-orange-500/5"
              }
            >
              {mode}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center border border-slate-700 overflow-hidden">
            <button
              onClick={() => setViewMode("overview")}
              data-testid="view-mode-overview"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase transition-colors ${
                viewMode === "overview"
                  ? "bg-orange-500/15 border-r border-slate-700 text-orange-400"
                  : "border-r border-slate-700 text-slate-500 hover:text-slate-300"
              }`}
            >
              <BarChart3 className="w-3 h-3" />
              Overview
            </button>
            <button
              onClick={() => setViewMode("detailed")}
              data-testid="view-mode-detailed"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase transition-colors ${
                viewMode === "detailed"
                  ? "bg-orange-500/15 text-orange-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <LayoutList className="w-3 h-3" />
              Detailed
            </button>
          </div>

          <button
            onClick={loadRecords}
            disabled={loading}
            data-testid="card-holders-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div
          className="px-4 py-3 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-4"
          data-testid="card-holders-error"
        >
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div
          className="text-orange-400 text-xs tracking-widest uppercase animate-pulse"
          data-testid="card-holders-loading"
        >
          Loading records…
        </div>
      )}

      {/* ── Detailed view footer: Add Card (all users) + Delete All (admin) ── */}
      {!loading && !error && viewMode === "detailed" && (
        <div className="flex justify-end items-center gap-3 mt-3">
          <button
            onClick={openAddCard}
            data-testid="card-holders-add-card"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add Card
          </button>

          {isAdmin === true && records.length > 0 && (
            <button
              onClick={openDeleteAll}
              data-testid="card-holders-delete-all"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Delete All
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && records.length === 0 && (
        <div
          className="border border-dashed border-slate-700 p-10 text-center text-slate-500 text-sm"
          data-testid="card-holders-empty"
        >
          No records found.
        </div>
      )}

      {/* ── Overview Mode ──────────────────────────────────────────────────── */}
      {!loading && !error && records.length > 0 && viewMode === "overview" && (
        <div className="overflow-x-auto" data-testid="card-holders-overview">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Card Type
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  User
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Count
                </th>
              </tr>
            </thead>
            <tbody>
              {overviewRows.map((row) => (
                <tr
                  key={`${row.baseName}|${row.username}`}
                  className="border-b border-slate-800/60 hover:bg-[#121822]/60 transition-colors"
                  data-testid="overview-row"
                >
                  <td className="px-4 py-3 text-slate-200 text-xs font-medium">
                    {row.baseName}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                    {getNicknameFromEmail(row.username, whitelistEntries)}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs font-mono tabular-nums">
                    {row.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detailed Mode ──────────────────────────────────────────────────── */}
      {!loading && !error && records.length > 0 && viewMode === "detailed" && (
        <div className="overflow-x-auto" data-testid="card-holders-table">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  User
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Card Name
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Location
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Timestamp
                </th>
                <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                  Proxy
                </th>
                {(isAdmin || currentUser) && (
                  <th className="text-left text-[10px] tracking-[0.25em] uppercase text-slate-500 px-4 py-2.5 font-normal">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {detailedRecords.map((record) => {
                const showDelete = canDelete(record);
                const showActions = showDelete;
                return (
                  <tr
                    key={record.id}
                    className="border-b border-slate-800/60 hover:bg-[#121822]/60 transition-colors"
                    data-testid="card-holder-row"
                  >
                    <td className="px-4 py-3 text-slate-200 text-xs font-mono">
                      {getNicknameFromEmail(record.username, whitelistEntries) ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs">
                      {record.cardName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {record.location ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-[10px] font-mono whitespace-nowrap">
                      {formatTimestamp(record.claimedAt)}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                      {record.proxyClaimedBy ?? (
                        <span className="text-slate-700">—</span>
                      )}
                    </td>
                    {(isAdmin || currentUser) && (
                      <td className="px-4 py-3">
                        {showActions && (
                          <div className="flex items-center gap-2">
                            {showDelete && (
                              <button
                                onClick={() => openDelete(record)}
                                data-testid="card-holder-delete"
                                aria-label="Delete record"
                                className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors text-[10px] tracking-widest uppercase"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Delete Confirm Modal ────────────────────────────────────── */}
      {deleteTarget && (
        <SimpleDialog
          open={!!deleteTarget}
          onOpenChange={(v) => !v && closeDelete()}
        >
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 max-w-md w-full text-white p-4"
            data-testid="card-holder-delete-modal"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
                Confirm Delete
              </div>
              <div className="font-display text-xl tracking-tight">
                Remove this record?
              </div>
              <div className="text-slate-400 text-sm mt-2">
                <span className="text-slate-200 font-medium">
                  {deleteTarget.username ?? "Unknown user"}
                </span>{" "}
                holding{" "}
                <span className="text-slate-200 font-medium">
                  {deleteTarget.cardName ?? "unknown card"}
                </span>{" "}
                at{" "}
                <span className="text-slate-200 font-medium">
                  {deleteTarget.location ?? "unknown location"}
                </span>
                . This action cannot be undone.
              </div>
            </div>

            {deleteError && (
              <div
                className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-4"
                data-testid="delete-error"
              >
                {deleteError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleteConfirming}
                data-testid="delete-cancel"
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteConfirming}
                data-testid="delete-confirm"
                className="flex items-center gap-1.5 px-5 py-2 border border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {deleteConfirming ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}

      {/* ── Delete All Confirm Modal ────────────────────────────────── */}
      {deleteAllOpen && (
        <SimpleDialog
          open={deleteAllOpen}
          onOpenChange={(v) => !v && closeDeleteAll()}
        >
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-red-500/40 max-w-md w-full text-white p-4"
            data-testid="card-holders-delete-all-modal"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-red-400 mb-1">
                Confirm Delete All
              </div>
              <div className="font-display text-xl tracking-tight">
                Delete all card holder records?
              </div>
              <div className="text-slate-400 text-sm mt-2">
                This will permanently remove{" "}
                <span className="text-red-300 font-semibold">
                  {records.length} {records.length === 1 ? "record" : "records"}
                </span>{" "}
                from the database. This action cannot be undone.
              </div>
            </div>

            {deleteAllError && (
              <div
                className="px-3 py-2 border border-red-500/30 bg-red-500/10 text-red-400 text-xs tracking-wide mb-4"
                data-testid="delete-all-error"
              >
                {deleteAllError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeDeleteAll}
                disabled={deleteAllConfirming}
                data-testid="delete-all-cancel"
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAll}
                disabled={deleteAllConfirming}
                data-testid="delete-all-confirm"
                className="flex items-center gap-1.5 px-5 py-2 border border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {deleteAllConfirming ? "Deleting…" : "Delete All"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}
      {/* ── Add Card Modal ──────────────────────────────────────────── */}
      {addCardOpen && (
        <SimpleDialog
          open={addCardOpen}
          onOpenChange={(v) => !v && closeAddCard()}
        >
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 max-w-md w-full text-white p-4"
            data-testid="card-holder-add-modal"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
                Add Card Record
              </div>
              <div className="font-display text-xl tracking-tight text-slate-200">
                New Card Holder
              </div>
            </div>

            <div className="space-y-3 mb-3">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  Username
                </label>
                {isAdmin ? (
                  <select
                    value={addFields.username}
                    onChange={(e) =>
                      setAddFields((f) => ({ ...f, username: e.target.value }))
                    }
                    data-testid="add-username"
                    className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm font-mono focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                  >
                    {whitelistEntries.length === 0 && (
                      <option value={addFields.username}>{addFields.username || "—"}</option>
                    )}
                    {whitelistEntries.map((entry) => (
                      <option key={entry.id ?? entry.email} value={entry.nickname || entry.email}>
                        {entry.nickname || entry.email}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={addFields.username}
                    readOnly
                    disabled
                    data-testid="add-username"
                    className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 text-slate-400 text-sm font-mono focus:outline-none cursor-not-allowed opacity-70"
                  />
                )}
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  Location
                </label>
                <select
                  value={addFields.location}
                  onChange={(e) => {
                    const newLocation = e.target.value;
                    const preset = PRESETS.find((p) => p.source === newLocation);
                    setAddFields((f) => ({
                      ...f,
                      location: newLocation,
                      cardName: preset ? preset.cardOptions[0] : "",
                    }));
                  }}
                  data-testid="add-location"
                  className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm font-mono focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                >
                  {PRESETS.map((p) => (
                    <option key={p.id} value={p.source}>
                      {p.source}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  Card Name
                </label>
                <select
                  value={addFields.cardName}
                  onChange={(e) =>
                    setAddFields((f) => ({ ...f, cardName: e.target.value }))
                  }
                  data-testid="add-card-name"
                  className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm font-mono focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                >
                  {(
                    PRESETS.find((p) => p.source === addFields.location)?.cardOptions ?? []
                  ).map((card) => (
                    <option key={card} value={card}>
                      {card}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
                  Proxy Claimed By{" "}
                  <span className="text-slate-600 normal-case tracking-normal">
                    (optional)
                  </span>
                </label>
                <select
                  value={addFields.proxy}
                  onChange={(e) =>
                    setAddFields((f) => ({ ...f, proxy: e.target.value }))
                  }
                  data-testid="add-proxy"
                  className="w-full px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm font-mono focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                >
                  <option value="">— None —</option>
                  {whitelistEntries.map((entry) => (
                    <option key={entry.id ?? entry.email} value={entry.nickname || entry.email}>
                      {entry.nickname || entry.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {addError && (
              <div
                className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-4"
                data-testid="add-card-error"
              >
                {addError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeAddCard}
                disabled={addSaving}
                data-testid="add-card-cancel"
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCard}
                disabled={addSaving}
                data-testid="add-card-submit"
                className="flex items-center gap-1.5 px-5 py-2 border border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
              >
                <Plus className="w-3.5 h-3.5" />
                {addSaving ? "Adding…" : "Add Card"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}
    </div>
  );
}
